import IntersectionObsManager from "../managers/IntersectionObserver.js";
import { onReducedMotionChange, prefersReducedMotion } from "../helpers/reducedMotion.js";

class HeroCycle {
    constructor(elem) {
        this.elem = elem;
        this.visualContainers = this.elem.querySelectorAll(".hero__visual");
        this.cycleTimeout = null;
        this.current = 0;
        this.isVisible = false;

        this.promptVideo = this.elem.querySelector(".hero-prompt-video video");
        this.promptTextElem = this.elem.querySelector(".hero-prompt-text");
        this.promptText = "get me the run of show for Northern wild’s july 31 show at beacon theatre";
        this.typeIndex = 0;
        this.typeSpeed = 25; // milliseconds per character

        this.init();
    }

    getDelay() {
        return this.current === 0 ? 8750 : 5000;
    }

    // Always clear first: the observer can report isIntersecting again without an
    // intervening exit, and a bare reassign would leak the previous timer and
    // stack cycles, speeding the carousel up permanently.
    scheduleCycle = () => {
        clearTimeout(this.cycleTimeout);
        this.cycleTimeout = setTimeout(this.cycle, this.getDelay());
    };

    // The preference is re-checked here, not just at the call sites, so that "never start
    // this video while reduced motion is on" holds regardless of event ordering — a change
    // event that arrives while the query is mid-transition cannot slip a playback through.
    //
    // Rejections are swallowed for the reason video.js swallows them on its ambient loop:
    // this video is decorative and its poster already describes the state, so nothing on
    // screen depends on the playback having started.
    playPrompt = () => {
        if (prefersReducedMotion()) return;

        // Short viewports hide the prompt overlays entirely (the `short` gate in
        // _hero.scss). A play() against the display: none video would still fetch,
        // decode and run the full MP4 for a layer that can never be seen — exactly the
        // bandwidth-constrained devices the landscape fix targets. getClientRects is
        // the render check itself, not a media-query string that could drift out of
        // sync with the CSS gates. If the viewport grows mid-cycle the video sits on
        // its poster until the cycle wraps back to slide 0, which restarts it.
        if (this.promptVideo.getClientRects().length === 0) return;

        const started = this.promptVideo.play();

        if (started) started.catch(() => {});
    };

    // Reduced motion gets the finished state, not a faster animation: the whole string in
    // one write, and the prompt video revealed on its poster frame instead of played. The
    // <video> carries no autoplay attribute for the same reason the promo in video.js does
    // not — a playback that has already started cannot be suppressed, only interrupted.
    //
    // Reading the preference here rather than at the call site also covers a flip that
    // lands mid-type: the next tick takes this branch and finishes the string at once.
    type = () => {
        // A prompt hidden by the short-viewport gate gets the same treatment as
        // reduced motion: the finished state in one write, instead of ~100 timers
        // typing into a node nobody can see. playPrompt() has its own render check,
        // so is-active only reveals the poster here; if the viewport later grows,
        // the cycle wraps back to slide 0 and types normally.
        if (prefersReducedMotion() || this.promptTextElem.getClientRects().length === 0) {
            this.promptTextElem.textContent = this.promptText;
            this.typeIndex = this.promptText.length;
            this.promptVideo.classList.add("is-active");
            return;
        }

        if (this.typeIndex < this.promptText.length) {
            this.promptTextElem.textContent += this.promptText.charAt(this.typeIndex);
            this.typeIndex++;
            setTimeout(this.type, this.typeSpeed);
        } else {
            this.promptVideo.classList.add("is-active");
            this.playPrompt();
        }
    };

    startPrompt = (reset = false) => {
        if (reset) {
            this.promptVideo.currentTime = 0;
            this.typeIndex = 0;
            this.promptTextElem.textContent = "";
        }

        this.type();
    };

    cycle = () => {
        const next = (this.current + 1) % this.visualContainers.length;

        if (next === 0) {
            this.startPrompt(true);
        } else {
            this.promptVideo.classList.remove("is-active");
        }

        this.visualContainers[this.current].classList.remove("is-active");
        this.visualContainers[next].classList.add("is-active");
        this.current = next;

        // wait longer when we're on the first slide
        this.scheduleCycle();
    };

    // Only the advancing is suppressed under reduced motion, not slide 1 itself — the
    // prompt still fills in, so the hero is composed rather than left half-built. With
    // scheduleCycle the single gate, `current` never leaves 0 and slides 2 and 3 never
    // come forward. Both keep their descriptive alt text in the accessibility tree:
    // opacity: 0 hides them visually without removing them.
    handleIntersection = (entry) => {
        this.isVisible = entry.isIntersecting;

        if (!this.isVisible) {
            clearTimeout(this.cycleTimeout);
            this.cycleTimeout = null;
            return;
        }

        if (!prefersReducedMotion()) this.scheduleCycle();

        if (this.current === 0) {
            this.startPrompt();
        }
    };

    // Two-way, matching video.js: turning the preference on has to cancel the timer
    // already pending, and turning it back off has to restart the loop — a hero already
    // in view might never see another intersection change to restart it. The typing timer
    // needs no cancelling; its next tick reads the preference and completes the string.
    handleMotionPreference = () => {
        if (prefersReducedMotion()) {
            clearTimeout(this.cycleTimeout);
            this.cycleTimeout = null;
            this.promptVideo.pause();
            return;
        }

        if (!this.isVisible) return;

        // The reduced-motion branch of type() leaves the video revealed but never started,
        // with typeIndex already at the end — so nothing re-enters type() to start it, and
        // it would sit frozen on frame 0 until cycle() wrapped back to slide 0 some 19s
        // later. is-active is the exact predicate: cycle() strips it on leaving slide 0, so
        // this only starts the video while it is the one actually on screen.
        if (this.promptVideo.classList.contains("is-active")) this.playPrompt();

        this.scheduleCycle();
    };

    init() {
        onReducedMotionChange(this.handleMotionPreference);

        this.observer = new IntersectionObsManager({
            rootMargin: "0% 0% 0% 0%",
            threshold: 0,
        });

        this.observer.observe(this.elem, this.handleIntersection);
    }
}

const heroElem = document.querySelector(".hero-cycle");
if (heroElem) new HeroCycle(heroElem);
